import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { formatEuro, formatPct } from '../lib/format';

function formatM2Centi(c: number): string {
  return `${(c / 100).toFixed(2).replace('.', ',')} m²`;
}

export function ReferencesPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const onlyAlerte = searchParams.get('seuil_alerte_depasse') === 'true';

  const query = useQuery({
    queryKey: ['references', q, onlyAlerte],
    queryFn: () =>
      api.references.list({
        q: q === '' ? undefined : q,
        seuil_alerte_depasse: onlyAlerte ? 'true' : undefined,
      }),
  });

  const updateParams = (next: Record<string, string>): void => {
    const sp = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v === '') sp.delete(k);
      else sp.set(k, v);
    }
    setSearchParams(sp, { replace: true });
  };

  return (
    <div className="max-w-7xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Références produit</h1>
          <p className="text-xs text-slate-500 mt-1">
            Catalogue gazon + stock disponible + marges.
          </p>
        </div>
        <Link to="/references/nouveau">
          <Button>+ Nouvelle référence</Button>
        </Link>
      </header>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <input
          placeholder="Recherche nom / code…"
          value={q}
          onChange={(e): void => updateParams({ q: e.target.value })}
          className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 flex-1 min-w-[200px]"
        />
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={onlyAlerte}
            onChange={(e): void =>
              updateParams({ seuil_alerte_depasse: e.target.checked ? 'true' : '' })
            }
          />
          Sous seuil uniquement
        </label>
      </div>

      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isError && <div className="text-sm text-red-400">Erreur de chargement.</div>}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          title="Aucune référence."
          description="Créez votre première référence produit."
          action={
            <Link to="/references/nouveau">
              <Button>+ Nouvelle référence</Button>
            </Link>
          }
        />
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Référence</th>
                <th className="px-4 py-2 text-right">Stock dispo</th>
                <th className="px-4 py-2 text-right">Rouleaux</th>
                <th className="px-4 py-2 text-right">Coût moyen</th>
                <th className="px-4 py-2 text-right">Valeur stock</th>
                <th className="px-4 py-2 text-right">Prix pro / rev / part.</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {query.data.items.map((r) => (
                <tr key={r.id} className="hover:bg-slate-900/60">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {r.alerte_sous_seuil && (
                        <span
                          className="inline-block w-2 h-2 bg-red-500 rounded-full"
                          title="Sous seuil"
                        />
                      )}
                      <Link
                        to={`/references/${String(r.id)}`}
                        className="text-slate-100 hover:text-floryon-green font-medium"
                      >
                        {r.nom}
                      </Link>
                    </div>
                    {r.code !== null && <div className="text-xs text-slate-500">{r.code}</div>}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-200">
                    {formatM2Centi(r.stock_dispo_centi_m2)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-400">{r.nb_rouleaux_dispo}</td>
                  <td className="px-4 py-2 text-right text-slate-300">
                    {r.cout_moyen_pondere_centimes_par_m2 !== null
                      ? `${formatEuro(r.cout_moyen_pondere_centimes_par_m2)}/m²`
                      : '—'}
                  </td>
                  <td className="px-4 py-2 text-right text-floryon-green">
                    {formatEuro(r.valeur_stock_centimes)}
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-slate-400">
                    {formatEuro(r.prix_pro_centimes_par_m2)} /{' '}
                    {formatEuro(r.prix_revendeur_centimes_par_m2)} /{' '}
                    {formatEuro(r.prix_particulier_centimes_par_m2)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/references/${String(r.id)}/rouleaux`}
                      className="text-xs text-floryon-green hover:underline"
                    >
                      Rouleaux →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-1 text-[10px] text-slate-600">
        ℹ️ {formatPct(0)} marge cible non affichée dans le tableau (cf. fiche détaillée).
      </div>
    </div>
  );
}
